import React, { useMemo } from 'react';
import { Box, Button, Divider, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useNavigate } from 'react-router-dom';
import { deriveCounterpartyInsights } from './counterpartyIntelligence';

const useStyles = makeStyles((theme) => ({
  panel: {
    background: 'rgba(10, 14, 30, 0.88)',
    border: '1px solid rgba(77, 121, 255, 0.22)',
    borderRadius: 14,
    padding: 14,
    textAlign: 'left',
    boxSizing: 'border-box',
    [theme.breakpoints.down('sm')]: {
      padding: 12,
    },
  },
  title: {
    fontSize: 12,
    fontWeight: 900,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: '#9ea4d8',
    marginBottom: 10,
  },
  divider: {
    background: 'rgba(255,255,255,0.08)',
    marginBottom: 12,
  },
  intro: {
    color: '#c9cff8',
    fontSize: 13,
    lineHeight: 1.55,
    marginBottom: 12,
  },
  summaryRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 12,
  },
  chip: {
    borderRadius: 999,
    border: '1px solid rgba(77, 121, 255, 0.28)',
    background: 'rgba(77, 121, 255, 0.12)',
    color: '#cfd6ff',
    fontSize: 11,
    fontWeight: 700,
    padding: '4px 10px',
  },
  chipStrong: {
    borderRadius: 999,
    border: '1px solid rgba(126, 161, 255, 0.34)',
    background: 'rgba(77, 121, 255, 0.18)',
    color: '#eef1ff',
    fontSize: 11,
    fontWeight: 800,
    padding: '4px 10px',
  },
  section: {
    marginBottom: 12,
  },
  sectionTitle: {
    color: '#9ea4d8',
    fontSize: 11,
    fontWeight: 800,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    marginBottom: 8,
  },
  seedRow: {
    borderRadius: 12,
    border: '1px solid rgba(77, 121, 255, 0.18)',
    background: 'rgba(12, 18, 38, 0.85)',
    padding: '10px 12px',
    marginBottom: 8,
  },
  seedTitle: {
    color: '#e5e7ff',
    fontSize: 13,
    fontWeight: 800,
    lineHeight: 1.4,
    wordBreak: 'break-word',
  },
  seedLink: {
    appearance: 'none',
    border: 'none',
    background: 'transparent',
    padding: 0,
    margin: 0,
    textAlign: 'left',
    cursor: 'pointer',
    fontFamily: 'inherit',
    '&:hover': {
      color: '#9fc0ff',
    },
  },
  seedMeta: {
    color: '#8d93c9',
    fontSize: 11,
    lineHeight: 1.35,
    marginTop: 4,
  },
  tagsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
    marginTop: 8,
  },
  tag: {
    borderRadius: 999,
    border: '1px solid rgba(255,255,255,0.12)',
    background: 'rgba(255,255,255,0.04)',
    color: '#d7dcff',
    fontSize: 10,
    fontWeight: 700,
    padding: '3px 8px',
    lineHeight: 1.2,
  },
  empty: {
    color: '#8d93c9',
    fontSize: 12,
    lineHeight: 1.45,
  },
  actions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 12,
  },
  actionButton: {
    textTransform: 'none',
    fontWeight: 800,
    borderRadius: 10,
    padding: '6px 14px',
  },
  secondaryButton: {
    textTransform: 'none',
    fontWeight: 700,
    borderRadius: 10,
    color: '#d9defa',
    borderColor: 'rgba(255,255,255,0.18)',
  },
  footer: {
    color: '#9ea4d8',
    fontSize: 12,
    lineHeight: 1.45,
    marginTop: 10,
  },
}));

function buildTokenContext(address) {
  const tokenAddress = String(address || '').trim();
  const params = new URLSearchParams();
  if (tokenAddress) params.set('tokenAddress', tokenAddress);
  return {
    search: params.toString() ? `?${params.toString()}` : '',
    state: {
      tokenAddress,
      from: `/token/${tokenAddress}`,
    },
  };
 }

export default function GraphPreviewPanel({ address, liquidity, stories, theses }) {
  const classes = useStyles();
  const navigate = useNavigate();
  const graphSeeds = useMemo(() => deriveCounterpartyInsights({ address, liquidity, stories, theses }), [address, liquidity, stories, theses]);
  const tokenContext = useMemo(() => buildTokenContext(address), [address]);
  const openTokenDetails = (targetAddress) => {
    const next = String(targetAddress || '').trim();
    if (!next) return;
    navigate(`/token/${next}`);
  };

  return (
    <Box className={classes.panel}>
      <Typography className={classes.title}>Counterparty Intelligence</Typography>
      <Divider className={classes.divider} />
      <Typography className={classes.intro}>
        Use linked story, thesis, and pool evidence to inspect the counterparties most likely to matter before jumping into the full graph.
      </Typography>

      <Box className={classes.summaryRow}>
        <Box className={classes.chipStrong}>Wallet Entities {graphSeeds.summary.walletCount}</Box>
        <Box className={classes.chipStrong}>Counterparty Tokens {graphSeeds.summary.tokenCount}</Box>
        <Box className={classes.chip}>Pool Links {graphSeeds.summary.poolCount}</Box>
        <Box className={classes.chip}>Route /links/{address ? String(address).slice(0, 6) : '—'}...</Box>
      </Box>

      <Box className={classes.section}>
        <Typography className={classes.sectionTitle}>Connected Wallet / Entity Seeds</Typography>
        {graphSeeds.wallets.length ? graphSeeds.wallets.map((row) => (
          <Box key={row.address} className={classes.seedRow}>
            <Typography
              component="button"
              type="button"
              className={`${classes.seedTitle} ${classes.seedLink}`}
              onClick={() => openTokenDetails(row.address)}
            >
              {row.address}
            </Typography>
            <Typography className={classes.seedMeta}>Mentioned across linked narrative evidence • Score {row.score}</Typography>
            {Array.isArray(row.reasons) && row.reasons.length ? (
              <Box className={classes.tagsRow}>
                {row.reasons.map((tag) => (
                  <Box key={tag} className={classes.tag}>{tag}</Box>
                ))}
              </Box>
            ) : null}
          </Box>
        )) : <Typography className={classes.empty}>No connected wallet seeds derived from current narrative inputs.</Typography>}
      </Box>

      <Box className={classes.section}>
        <Typography className={classes.sectionTitle}>Counterparty Tokens</Typography>
        {graphSeeds.tokens.length ? graphSeeds.tokens.map((row) => (
          <Box key={row.address} className={classes.seedRow}>
            <Typography
              component="button"
              type="button"
              className={`${classes.seedTitle} ${classes.seedLink}`}
              onClick={() => openTokenDetails(row.address)}
            >
              {row.address}
            </Typography>
            <Typography className={classes.seedMeta}>Counterparty token candidate • Score {row.score}</Typography>
            {Array.isArray(row.reasons) && row.reasons.length ? (
              <Box className={classes.tagsRow}>
                {row.reasons.map((tag) => (
                  <Box key={tag} className={classes.tag}>{tag}</Box>
                ))}
              </Box>
            ) : null}
          </Box>
        )) : <Typography className={classes.empty}>No counterparty token seeds derived yet.</Typography>}
      </Box>

      <Box className={classes.section}>
        <Typography className={classes.sectionTitle}>Pool / Pair Relationships</Typography>
        {graphSeeds.pools.length ? graphSeeds.pools.map((row) => (
          <Box key={row.address} className={classes.seedRow}>
            <Typography
              component="button"
              type="button"
              className={`${classes.seedTitle} ${classes.seedLink}`}
              onClick={() => openTokenDetails(row.address)}
            >
              {row.address}
            </Typography>
            <Typography className={classes.seedMeta}>Pool-linked context • Score {row.score}</Typography>
            {Array.isArray(row.reasons) && row.reasons.length ? (
              <Box className={classes.tagsRow}>
                {row.reasons.map((tag) => (
                  <Box key={tag} className={classes.tag}>{tag}</Box>
                ))}
              </Box>
            ) : null}
          </Box>
        )) : <Typography className={classes.empty}>No direct pool relationship surfaced from current liquidity context.</Typography>}
      </Box>

      <Box className={classes.actions}>
        <Button className={classes.actionButton} variant="contained" color="primary" onClick={() => navigate(`/links/${address || ''}`)} disabled={!address}>Open Full Graph</Button>
        <Button className={classes.secondaryButton} variant="outlined" onClick={() => navigate(`/stories${tokenContext.search}`, { state: tokenContext.state })}>Open Counterparty Evidence</Button>
        <Button className={classes.secondaryButton} variant="outlined" onClick={() => navigate(`/thesis${tokenContext.search}`, { state: tokenContext.state })}>Open Theses</Button>
      </Box>

      <Typography className={classes.footer}>
        Counterparty intelligence is inferred from token-linked stories, thesis evidence, and pool context, then packaged as a fast triage layer before deeper graph investigation.
      </Typography>
    </Box>
  );
}
